import { useEffect, useState } from "react";
import { productImageUrl } from "@/utils/format.js";
import "@/components/order-thumbnail.css";

/** Phones only — tablets and up show the full preview set. */
const MOBILE_ORDER_THUMB_MQ = "(max-width: 768px)";
const MAX_THUMBS_MOBILE = 2;
const MAX_THUMBS_DESKTOP = 6;

function resolvePreviewPhotoIds(order, maxCount) {
    if (!order?.previewPhotoIds?.length) {
        return [];
    }
    return order.previewPhotoIds.filter(Boolean).slice(0, maxCount);
}

function useMaxOrderThumbs() {
    const [maxThumbs, setMaxThumbs] = useState(() => {
        if (typeof window === "undefined") {
            return MAX_THUMBS_DESKTOP;
        }
        return window.matchMedia(MOBILE_ORDER_THUMB_MQ).matches
            ? MAX_THUMBS_MOBILE
            : MAX_THUMBS_DESKTOP;
    });

    useEffect(() => {
        const media = window.matchMedia(MOBILE_ORDER_THUMB_MQ);
        const sync = () => {
            setMaxThumbs(media.matches ? MAX_THUMBS_MOBILE : MAX_THUMBS_DESKTOP);
        };
        sync();
        media.addEventListener("change", sync);
        return () => media.removeEventListener("change", sync);
    }, []);

    return maxThumbs;
}

/**
 * Compact order preview: product primary photos (one per product).
 * - list: side by side; up to 2 on phones, up to 6 on larger screens (squares)
 * - home: stacked vertically; always up to 2 (circles), matching the tall progress card
 */
export default function OrderThumbnail({ order, variant = "list", className = "" }) {
    const listMaxThumbs = useMaxOrderThumbs();
    const maxThumbs = variant === "home" ? MAX_THUMBS_MOBILE : listMaxThumbs;
    const photoIds = resolvePreviewPhotoIds(order, maxThumbs);
    const stackClass = [
        "order-thumb-stack",
        `order-thumb-stack--${variant}`,
        `order-thumb-stack--count-${photoIds.length || 0}`,
        className
    ].filter(Boolean).join(" ");

    if (photoIds.length === 0) {
        const letter = (order?.previewTitle || "C").slice(0, 1).toUpperCase();
        return (
            <div className={stackClass} aria-hidden="true">
                <div className="order-thumb-stack-item order-thumb-stack-item--empty">{letter}</div>
            </div>
        );
    }

    return (
        <div className={stackClass} aria-hidden="true">
            {photoIds.map((photoId, index) => (
                <img
                    key={`${photoId}-${index}`}
                    src={productImageUrl(photoId, { size: "thumb" })}
                    alt=""
                    className="order-thumb-stack-item"
                    loading="lazy"
                    decoding="async"
                />
            ))}
        </div>
    );
}
